import * as React from 'react'
import { AppearanceContext } from './champion-icon'
import { cardBackground, type BracketAppearance } from './appearance'

import { ChampionNode } from './champion-node'
import { ColumnHeaderRail } from './column-header-rail'
import { ConnectorLayer } from './connector-layer'
import {
  BRACKET_COLUMN_GAP,
  BRACKET_ROW_GAP,
  BRACKET_ROW_HEIGHT,
  BRACKET_TOP_PADDING,
} from './constants'
import {
  getMatchBox,
  getVerticalMatchBox,
  getVerticalChampionBox,
  toCssBox,
} from './layout'
import { MatchNode } from './match-node'
import { isBracketBoxVisible, type BracketVisibleArea } from './viewport'
import type {
  BracketDirection,
  BracketDisplayMode,
  BracketMakerCopy,
  BracketStructure,
  BracketTheme,
  ChampionPlacement,
  ColumnHeader,
  ConnectorLine,
  Match,
  PositionedMatch,
  WinnerSide,
} from './types'

type BracketCanvasProps = {
  appearance: BracketAppearance
  bracketHeight: number
  bracketWidth: number
  bracketTitle: string
  champion: string
  championPlacement: ChampionPlacement | null
  columnHeaders: ColumnHeader[]
  columnOffsets: number[]
  columnWidths: number[]
  connectors: ConnectorLine[]
  copy: BracketMakerCopy
  direction: BracketDirection
  displayMode: BracketDisplayMode
  matchLayout: PositionedMatch[][]
  normalizedTeamCount: number
  seedOrder: number[] | null
  onClearSeedImage: (index: number) => void
  onPickWinner: (
    round: number,
    match: number,
    side: WinnerSide,
    name: string,
  ) => void
  onRenameSeed: (index: number, value: string) => void
  onRequestSeedImage: (index: number) => void
  rounds: Match[][]
  showNames: boolean
  structure: BracketStructure
  theme: BracketTheme
  verticalContentWidth: number
  verticalReverse: boolean
  verticalYAxisLength: number
  verticalXOffset: number
  visibleArea: BracketVisibleArea | null
}

export const BracketCanvas = React.memo(function BracketCanvas({
  appearance,
  bracketHeight,
  bracketWidth,
  bracketTitle,
  champion,
  championPlacement,
  columnHeaders,
  columnOffsets,
  columnWidths,
  connectors,
  copy,
  direction,
  displayMode,
  matchLayout,
  normalizedTeamCount,
  seedOrder,
  onClearSeedImage,
  onPickWinner,
  onRenameSeed,
  onRequestSeedImage,
  rounds,
  showNames,
  structure,
  theme,
  verticalContentWidth,
  verticalReverse,
  verticalYAxisLength,
  verticalXOffset,
  visibleArea,
}: BracketCanvasProps) {
  const focusedKey =
    typeof document !== 'undefined'
      ? document.activeElement
          ?.closest('[data-bracket-match]')
          ?.getAttribute('data-bracket-match')
      : undefined
  const championStyle =
    championPlacement && direction === 'vertical'
      ? toCssBox(
          getVerticalChampionBox(
            championPlacement,
            columnOffsets,
            columnWidths,
            verticalXOffset,
            verticalYAxisLength,
            verticalReverse,
          ),
        )
      : undefined

  return (
    <AppearanceContext.Provider value={appearance}>
      <div
        className="bracket-artwork absolute inset-0"
        data-custom-text={!!appearance.textColor}
        data-custom-lines={!!appearance.connectorColor}
        data-custom-card={
          !!appearance.cardColor || appearance.cardOpacity !== 1
        }
        style={
          {
            '--bracket-text': appearance.textColor ?? undefined,
            '--bracket-lines': appearance.connectorColor ?? undefined,
            '--bracket-card':
              appearance.cardColor || appearance.cardOpacity !== 1
                ? cardBackground(appearance)
                : undefined,
            color: appearance.textColor ?? undefined,
            printColorAdjust: 'exact',
            WebkitPrintColorAdjust: 'exact',
          } as React.CSSProperties
        }
      >
        <ColumnHeaderRail
          bracketTitle={bracketTitle}
          champion={champion}
          columnHeaders={columnHeaders}
          columnOffsets={columnOffsets}
          columnWidths={columnWidths}
          copy={copy}
          direction={direction}
          roundCount={rounds.length}
          showNames={showNames}
          theme={theme}
          verticalContentWidth={verticalContentWidth}
          verticalReverse={verticalReverse}
          verticalYAxisLength={verticalYAxisLength}
          verticalXOffset={verticalXOffset}
        />
        <ConnectorLayer
          color={appearance.connectorColor}
          connectors={connectors}
          height={bracketHeight}
          theme={theme}
          width={bracketWidth}
        />
        <div
          className={direction === 'horizontal' ? 'grid' : 'absolute inset-0'}
          style={
            direction === 'horizontal'
              ? {
                  gridTemplateColumns: columnWidths
                    .map((width) => `${width}px`)
                    .join(' '),
                  gridAutoRows: `${BRACKET_ROW_HEIGHT}px`,
                  columnGap: `${BRACKET_COLUMN_GAP}px`,
                  rowGap: `${BRACKET_ROW_GAP}px`,
                  paddingTop: `${BRACKET_TOP_PADDING}px`,
                }
              : undefined
          }
        >
          {rounds.map((round, roundIndex) =>
            round.map((match, matchIndex) => {
              const layout = matchLayout[roundIndex]?.[matchIndex]
              if (!layout) return null
              const box =
                direction === 'horizontal'
                  ? getMatchBox(layout, columnOffsets, columnWidths)
                  : getVerticalMatchBox(
                      layout,
                      columnOffsets,
                      columnWidths,
                      verticalXOffset,
                      verticalYAxisLength,
                      verticalReverse,
                    )
              const key = `${roundIndex}-${matchIndex}`
              // 保留焦点所在卡片，缩放或窗口改变时不打断队名编辑。
              const focused = focusedKey === key
              return (
                <MatchNode
                  key={key}
                  copy={copy}
                  direction={direction}
                  displayMode={displayMode}
                  layout={layout}
                  match={match}
                  matchIndex={matchIndex}
                  normalizedTeamCount={normalizedTeamCount}
                  seedOrder={seedOrder}
                  onClearSeedImage={onClearSeedImage}
                  onPickWinner={onPickWinner}
                  onRenameSeed={onRenameSeed}
                  onRequestSeedImage={onRequestSeedImage}
                  roundCount={rounds.length}
                  roundIndex={roundIndex}
                  showNames={showNames}
                  structure={structure}
                  style={direction === 'vertical' ? toCssBox(box) : undefined}
                  theme={theme}
                  visible={
                    normalizedTeamCount < 64 ||
                    focused ||
                    isBracketBoxVisible(box, visibleArea)
                  }
                />
              )
            }),
          )}
          {structure === 'single' && championPlacement ? (
            <ChampionNode
              champion={champion}
              copy={copy}
              direction={direction}
              placement={championPlacement}
              showNames={showNames}
              style={championStyle}
              theme={theme}
            />
          ) : null}
        </div>
      </div>
    </AppearanceContext.Provider>
  )
})
